import React, { createContext, useContext, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Toast from './Toast'
import type { ToastVariant } from './Toast'

export type ToastMessage = string | { key: string; params?: Record<string, unknown> }

interface ToastItem {
  id: string
  message: ToastMessage
  variant: ToastVariant
  duration?: number
}

interface ToastContextValue {
  addToast: (message: ToastMessage, variant?: ToastVariant, duration?: number) => void
  removeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

export interface ToastProviderProps {
  children: React.ReactNode
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const { t } = useTranslation()

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const addToast = useCallback(
    (message: ToastMessage, variant: ToastVariant = 'info', duration?: number) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
      setToasts((prev) => [...prev, { id, message, variant, duration }])
    },
    []
  )

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex flex-col-reverse gap-2 items-center pointer-events-none">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast
              variant={toast.variant}
              message={
                typeof toast.message === 'string'
                  ? toast.message
                  : t(toast.message.key, toast.message.params)
              }
              duration={toast.duration}
              onDismiss={() => removeToast(toast.id)}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
